import React, { useState, useEffect, useRef } from 'react';
import { useMap, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function createUuidV4() {
  // Prefer standards-based UUIDs when available.
  // Note: crypto.randomUUID may be unavailable in non-secure contexts (HTTP) or older browsers.
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (webCrypto?.randomUUID) {
    return webCrypto.randomUUID();
  }

  // RFC4122 v4 using getRandomValues when possible.
  if (webCrypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort (not cryptographically strong): fine for client-side temporary IDs.
  return `uuid-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const planningPinIcon = L.divIcon({
  className: "", // important: prevent default leaflet styles
  html: `
    <svg width="33" height="34" viewBox="0 0 33 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_1420_9083)">
        <mask id="mask0_1420_9083" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
          <rect width="32" height="32" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_1420_9083)">
          <path d="M15.9997 15.9998C16.733 15.9998 17.3608 15.7387 17.883 15.2165C18.4052 14.6943 18.6663 14.0665 18.6663 13.3332C18.6663 12.5998 18.4052 11.9721 17.883 11.4498C17.3608 10.9276 16.733 10.6665 15.9997 10.6665C15.2663 10.6665 14.6386 10.9276 14.1163 11.4498C13.5941 11.9721 13.333 12.5998 13.333 13.3332C13.333 14.0665 13.5941 14.6943 14.1163 15.2165C14.6386 15.7387 15.2663 15.9998 15.9997 15.9998ZM15.9997 29.3332C12.4219 26.2887 9.74967 23.4609 7.98301 20.8498C6.21634 18.2387 5.33301 15.8221 5.33301 13.5998C5.33301 10.2665 6.40523 7.61095 8.54967 5.63317C10.6941 3.65539 13.1775 2.6665 15.9997 2.6665C18.8219 2.6665 21.3052 3.65539 23.4497 5.63317C25.5941 7.61095 26.6663 10.2665 26.6663 13.5998C26.6663 15.8221 25.783 18.2387 24.0163 20.8498C22.2497 23.4609 19.5775 26.2887 15.9997 29.3332Z" fill="#6711A0"/>
        </g>
      </g>
      <defs>
        <filter id="filter0_d_1420_9083" x="5.23301" y="0.566504" width="27.533" height="32.8665" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="3" dy="1"/>
          <feGaussianBlur stdDeviation="1.55"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1420_9083"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1420_9083" result="shape"/>
        </filter>
      </defs>
    </svg>
  `,
  iconSize: [33, 34],
  // tip of the pin is near bottom-center; tweak if needed
  iconAnchor: [16, 32],
  popupAnchor: [0, -30],
});

export default function RoutePlanner({ isActive, onRouteCreated, onReset }) {
  const map = useMap();
  const [waypoints, setWaypoints] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);
  
  const abortControllerRef = useRef(null);
  const onRouteCreatedRef = useRef(onRouteCreated);

  // Keep ref updated
  useEffect(() => {
    onRouteCreatedRef.current = onRouteCreated;
  }, [onRouteCreated]);

  useEffect(() => {
    if (waypoints.length < 2) {
      onRouteCreated?.(null);
      onReset?.();
    }
  }, [onReset, onRouteCreated, waypoints.length]);

  useEffect(() => {
    if (!isActive) {
      // Clear when planner is deactivated
      setWaypoints([]);
      setRouteCoordinates([]);
      setError(null);
      setIsCalculating(false);
      return;
    }

    // Add click handler to add waypoints
    const handleMapClick = (e) => {
      const target = e?.originalEvent?.target;

      // Ignore clicks coming from Leaflet UI (popups, controls, etc.)
      if (
        target?.closest?.(".leaflet-popup") ||
        target?.closest?.(".leaflet-control") ||
        target?.closest?.(".leaflet-pane")?.classList?.contains("leaflet-popup-pane")
      ) {
        return;
      }

      const { lat, lng } = e.latlng;

      setWaypoints((prev) => [
        ...prev,
        {
          id: createUuidV4(),
          lat,
          lng,
          latlng: [lat, lng],
        },
      ]);

      setError(null);
    };

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [isActive, map]);

  // Calculate route when waypoints change (with debounce)
  useEffect(() => {
    if (waypoints.length < 2) {
      setRouteCoordinates([]);
      setError(null);
      setIsCalculating(false);
      return;
    }

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Debounce: wait 1000ms before calculating
    const debounceTimer = setTimeout(async () => {
      setIsCalculating(true);
      setError(null);

      // Use OSRM (OpenStreetMap Routing Machine) API with timeout
      const coords = waypoints.map(w => `${w.lng},${w.lat}`).join(';');
      const url = `https://router.project-osrm.org/route/v1/cycling/${coords}?overview=full&geometries=geojson`;

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 15000); // 15 second timeout

      try {
        const res = await fetch(url, { signal: abortControllerRef.current.signal });
        clearTimeout(timeoutId);
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();
        
        if (data.code === 'Ok' && data.routes && data.routes[0]) {
          const route = data.routes[0];
          const coordinates = route.geometry.coordinates.map(c => [c[1], c[0]]);
          const distance = route.distance / 1000; // Convert to km
          const duration = route.duration; // In seconds
          
          setRouteCoordinates(coordinates);
          
          // Call onRouteCreated using ref (doesn't trigger re-render)
          if (onRouteCreatedRef.current) {
            onRouteCreatedRef.current({
              waypoints: waypoints.map(w => ({ lat: w.lat, lng: w.lng })),
              coordinates: coordinates,
              distance_km: distance,
              duration_seconds: duration,
            });
          }
          
          setError(null);
        } else {
          throw new Error(data.message || 'Route calculation failed');
        }
        
        setIsCalculating(false);
      } catch (err) {
        clearTimeout(timeoutId);
        
        // Don't show error if request was aborted (user is still clicking)
        if (err.name === 'AbortError') {
          setIsCalculating(false);
          return;
        }
        
        console.error('Route calculation failed:', err);
        
        setError('Failed to calculate route. Please try again.');
        setIsCalculating(false);
        setRouteCoordinates([]);
      }
    }, 1000); // Increased debounce to 1 second

    return () => {
      clearTimeout(debounceTimer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [waypoints]); // Removed onRouteCreated from dependencies

  // const handleRemoveWaypoint = (index) => {
  //   setWaypoints(prev => prev.filter((_, i) => i !== index));
  //   setError(null);
  // };

  const handleRemoveWaypoint = (id) => {
    map.closePopup();
    setWaypoints((prev) => prev.filter((wp) => wp.id !== id));
    setError(null);
  };

  const handleClearAll = () => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    map.closePopup();
    setWaypoints([]);
    setRouteCoordinates([]);
    setError(null);
    setIsCalculating(false);
    onRouteCreated?.(null);
    onReset?.();
  };

  if (!isActive) return null;

  return (
    <>
      {/* Waypoint Markers */}
      {waypoints.map((wp, index) => (
        <Marker key={wp.id} position={wp.latlng} icon={planningPinIcon}>
          <Popup closeOnClick={true} autoClose={true} closeButton={true}>
            <div className="text-center">
              <p className="font-semibold mb-2">
                {index === 0 ? '🏁 Start' : index === waypoints.length - 1 ? '🎯 End' : `📍 Waypoint ${index}`}
              </p>
              <div className="space-y-1">
                <button
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    ev.nativeEvent?.stopPropagation?.(); // <-- important for Leaflet native event
                    handleRemoveWaypoint(wp.id);
                  }}
                  className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 w-full"
                >
                  Remove
                </button>
                {waypoints.length > 0 && (
                  <button
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      ev.nativeEvent?.stopPropagation?.();
                      handleClearAll();
                    }}
                    className="text-xs bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 w-full"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Route Line */}
      {routeCoordinates.length > 0 && (
        <Polyline
          positions={routeCoordinates}
          color="#BC74EC"
          weight={4}
          opacity={0.9}
        />
      )}

      {/* Status indicators */}
      {isCalculating && waypoints.length >= 2 && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid #10B981',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          Calculating route...
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {error && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#FEE2E2',
          color: '#991B1B',
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '90%',
        }}>
          ⚠️ {error}
        </div>
      )}
    </>
  );
}